/**
 * Data Source Adapter Factory
 * 
 * Central registry for managing different data source adapters.
 * Provides a clean way to register and retrieve adapters based on data source type.
 */

import { IDataSourceAdapter, IAdapterFactory } from './IDataSourceAdapter';

class AdapterFactory implements IAdapterFactory {
  private adapters: Map<string, IDataSourceAdapter> = new Map();

  /**
   * Register an adapter for a specific data source type
   */
  registerAdapter(type: string, adapter: IDataSourceAdapter): void {
    console.log(`[AdapterFactory] Registering adapter for data source type: ${type}`);
    this.adapters.set(type.toLowerCase(), adapter);
  }

  /**
   * Get an adapter for a specific data source type
   */
  getAdapter(type: string): IDataSourceAdapter {
    const normalizedType = type.toLowerCase();
    const adapter = this.adapters.get(normalizedType);
    
    if (!adapter) {
      throw new Error(`No adapter registered for data source type: ${type}. Available types: ${this.getRegisteredTypes().join(', ')}`);
    }

    return adapter;
  }

  /**
   * Check if an adapter is registered for a given type
   */
  hasAdapter(type: string): boolean {
    return this.adapters.has(type.toLowerCase());
  }

  /**
   * Get all registered adapter types
   */
  getRegisteredTypes(): string[] {
    return Array.from(this.adapters.keys());
  }

  /**
   * Unregister an adapter (useful for testing)
   */
  unregisterAdapter(type: string): boolean {
    return this.adapters.delete(type.toLowerCase());
  }

  /**
   * Clear all registered adapters (useful for testing)
   */
  clearAdapters(): void {
    this.adapters.clear();
  }
}

// Export singleton instance
export const adapterFactory = new AdapterFactory();

// Export class for testing
export { AdapterFactory };
